import React, { useCallback, useEffect, useRef, useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import { WhatsappTemplateItemType } from "../../../services/WhatsappService";
import { ToastService } from "../../../pages/shared/bootstrap/Toast";
import { TemplatePopover } from "../TemplatePopover";

const MAX_LINES = 5;

type WhatsappInputSendProps = {
  handleSend: () => void;
  whatsAppTemplateText: string | null;
  setFreeTextChanged: (data: string) => void;
  disabledInput?: boolean;
  cleanFreeText: boolean;
  loading: boolean;
  openchat: boolean;
  onPickTemplate: (item: WhatsappTemplateItemType | "0") => void;
};

export const WhatsappInputSend: React.FC<WhatsappInputSendProps> = ({
  handleSend,
  whatsAppTemplateText,
  setFreeTextChanged,
  disabledInput = true,
  cleanFreeText,
  loading,
  openchat,
  onPickTemplate
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const templateBtnRef = useRef<HTMLButtonElement | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const previousTemplateTextRef = useRef<string | null>(null);

  const adjustTextAreaHeight = useCallback(() => {
    if (!textAreaRef.current || typeof window === "undefined") return;

    const textarea = textAreaRef.current;
    textarea.style.height = "auto";
    const fullHeight = textarea.scrollHeight;
    const computed = window.getComputedStyle(textarea);
    const lineHeightValue = parseFloat(computed.lineHeight || "0");
    const fontSizeValue = parseFloat(computed.fontSize || "0");
    const paddingTop = parseFloat(computed.paddingTop || "0");
    const paddingBottom = parseFloat(computed.paddingBottom || "0");
    const borderTop = parseFloat(computed.borderTopWidth || "0");
    const borderBottom = parseFloat(computed.borderBottomWidth || "0");

    const fallbackLineHeight = fontSizeValue > 0 ? fontSizeValue * 1.2 : 20;
    const lineHeight = lineHeightValue || fallbackLineHeight;

    // Calcular altura máxima para 5 líneas
    const maxHeight =
      lineHeight * MAX_LINES +
      paddingTop +
      paddingBottom +
      borderTop +
      borderBottom;
    const minHeight =
      lineHeight + paddingTop + paddingBottom + borderTop + borderBottom;
    const newHeight = Math.max(minHeight, Math.min(fullHeight, maxHeight));

    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = fullHeight > maxHeight ? "auto" : "hidden";
    textarea.style.maxHeight = `${maxHeight}px`;
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFreeTextChanged(e.target.value);
    adjustTextAreaHeight();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Evita el salto de línea
      if (
        (disabledInput && !whatsAppTemplateText) ||
        (!disabledInput && !whatsAppTemplateText)
      ) {
        ToastService.showToast(
          TranslationService.translate.SelectATemplateAndFillRequiredFields,
          "",
          "info"
        );
        return;
      }
      if (!loading) handleSend();
    }
  };

  useEffect(() => {
    if (!disabledInput && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [disabledInput]);

  useEffect(() => {
    if (cleanFreeText && textAreaRef.current) {
      textAreaRef.current.value = "";
      setFreeTextChanged("");
    }
  }, [cleanFreeText, setFreeTextChanged]);

  useEffect(() => {
    adjustTextAreaHeight();
  }, [whatsAppTemplateText, disabledInput, adjustTextAreaHeight]);

  // Cerrar el popover de templates solo cuando el template se resetea (cambia de tener valor a vacío)
  useEffect(() => {
    const hadValue =
      previousTemplateTextRef.current &&
      previousTemplateTextRef.current.length > 0;
    const nowEmpty = !whatsAppTemplateText || whatsAppTemplateText.length === 0;

    // Solo cerrar si tenía valor y ahora está vacío (reset)
    if (hadValue && nowEmpty && showTemplates) {
      setShowTemplates(false);
    }

    // Actualizar el valor anterior
    previousTemplateTextRef.current = whatsAppTemplateText;
  }, [whatsAppTemplateText, showTemplates]);

  const onSendClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (
      (disabledInput && !whatsAppTemplateText) ||
      (!disabledInput && !whatsAppTemplateText)
    ) {
      ToastService.showToast(
        TranslationService.translate.SelectATemplateAndFillRequiredFields,
        "",
        "info"
      );
      return;
    }
    if (!loading) handleSend();
  };

  if (loading) {
    return null;
  }

  return (
    <>
      <div
        className="wa-input-row"
        style={{ display: "flex", alignItems: "flex-end", gap: "10px" }}
      >
        <div
          className="wa-container-chat flex-grow-1"
          style={{ display: "flex", alignItems: "flex-end", gap: "8px" }}
        >
          {!openchat && (
            <button
              ref={templateBtnRef}
              type="button"
              className="wa-template-btn"
              onClick={() => setShowTemplates((v) => !v)}
              title={TranslationService.translate.WhatsAppTemplate}
              aria-label={TranslationService.translate.WhatsAppTemplate}
              style={{ alignSelf: "flex-end", marginBottom: "10px" }}
            >
              <i
                className="fa-regular fa-file-lines"
                style={{ fontSize: 15, color: "rgb(97, 97, 97)" }}
              />
            </button>
          )}

          <textarea
            ref={textAreaRef}
            name="input"
            className="wa-input form-control"
            placeholder={
              disabledInput
                ? TranslationService.translate.SelectTemplate
                : TranslationService.translate.WriteMessage
            }
            autoComplete="off"
            readOnly={disabledInput}
            aria-readonly={disabledInput}
            value={whatsAppTemplateText ?? ""}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            style={{ resize: "none", overflowY: "auto" }}
          />
        </div>

        <div
          className="conversation-compose"
          style={{ flexShrink: 0, alignSelf: "flex-end" }}
        >
          <button
            className="send"
            onClick={onSendClick}
            style={{ marginBottom: "4px" }}
          >
            <div className="circle">
              {!loading ? (
                <i className="fas fa-send"></i>
              ) : (
                <i
                  className="fas fa-spinner-third fa-spin"
                  style={{ marginRight: 5 }}
                />
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Popover flotante con la lista de templates - solo si no está en modo texto libre */}
      {!openchat && (
        <TemplatePopover
          open={showTemplates}
          onClose={() => setShowTemplates(false)}
          anchorRef={templateBtnRef}
          onPick={(item) => {
            onPickTemplate(item);
            setShowTemplates(false);
            if (item === "0" && textAreaRef.current) {
              setTimeout(() => textAreaRef.current?.focus(), 0);
            }
          }}
          openchat={openchat}
        />
      )}
    </>
  );
};
